import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Mail, Phone, User, Download, Trash2, Eye, Link as LinkIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCurriculos = () => {
  const [curriculos, setCurriculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchCurriculos();
  }, []);

  const fetchCurriculos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/curriculos`);
      setCurriculos(response.data);
    } catch (error) {
      console.error('Erro ao buscar currículos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este currículo? O arquivo também será apagado.')) return;
    try {
      await axios.delete(`${API}/curriculos/${id}`);
      setCurriculos((prev) => prev.filter((c) => c.id !== id));
    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert('Erro ao excluir currículo');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filtered = curriculos.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.nome || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.telefone || '').toLowerCase().includes(q) ||
      (c.mensagem || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <Link to="/admin" className="text-gray-600 hover:text-gray-900" data-testid="admin-curriculos-back">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900">Currículos Recebidos</h1>
              <p className="text-sm text-gray-500">Ordenados do mais recente para o mais antigo</p>
            </div>
            <span
              className="bg-purple-100 text-purple-700 text-sm font-semibold px-3 py-1 rounded-full"
              data-testid="admin-curriculos-total"
            >
              {loading ? '...' : `${curriculos.length} total`}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, email, telefone..."
            data-testid="admin-curriculos-search"
            className="w-full max-w-md px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando currículos...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-20 text-center" data-testid="admin-curriculos-empty">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-2xl text-gray-600">
              {search ? 'Nenhum currículo encontrado para a busca' : 'Nenhum currículo recebido ainda'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((c) => (
              <div
                key={c.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6"
                data-testid={`admin-curriculo-card-${c.id}`}
              >
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-x-6 gap-y-2 mb-4">
                      <div className="flex items-center space-x-2 text-gray-700">
                        <User className="w-5 h-5 text-purple-600" />
                        <span className="font-semibold">{c.nome}</span>
                      </div>
                      <a
                        href={`tel:${c.telefone}`}
                        className="flex items-center space-x-2 text-gray-600 hover:text-purple-600"
                      >
                        <Phone className="w-4 h-4" />
                        <span className="text-sm">{c.telefone}</span>
                      </a>
                      <a
                        href={`mailto:${c.email}`}
                        className="flex items-center space-x-2 text-gray-600 hover:text-purple-600"
                      >
                        <Mail className="w-4 h-4" />
                        <span className="text-sm">{c.email}</span>
                      </a>
                    </div>
                    {c.mensagem && (
                      <p className="text-gray-700 mb-2 whitespace-pre-wrap">{c.mensagem}</p>
                    )}
                    {c.arquivo_nome ? (
                      <div className="flex items-center flex-wrap gap-3 text-sm text-gray-500 mb-2">
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4" />
                          <span>{c.arquivo_nome}</span>
                        </div>
                        {c.arquivo_url && (
                          <button
                            type="button"
                            onClick={() => navigator.clipboard?.writeText(`${BACKEND_URL}${c.arquivo_url}`)}
                            title="Copiar URL pública do arquivo"
                            data-testid={`admin-curriculo-copy-url-${c.id}`}
                            className="inline-flex items-center space-x-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded"
                          >
                            <LinkIcon className="w-3 h-3" />
                            <span>Copiar URL pública</span>
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-amber-600 italic mb-2">Sem arquivo anexado</p>
                    )}
                    <p className="text-xs text-gray-500">Recebido em: {formatDate(c.created_at)}</p>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    {c.arquivo_nome && (
                      <>
                        <a
                          href={`${API}/curriculos/${c.id}/view`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`admin-curriculo-view-${c.id}`}
                          className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          <span>Visualizar Currículo</span>
                        </a>
                        <a
                          href={`${API}/curriculos/${c.id}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`admin-curriculo-download-${c.id}`}
                          className="inline-flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          <span>Baixar</span>
                        </a>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(c.id)}
                      data-testid={`admin-curriculo-delete-${c.id}`}
                      className="inline-flex items-center space-x-2 bg-red-50 hover:bg-red-100 text-red-700 text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Excluir</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCurriculos;
